import React from 'react';
import { FormikValues } from 'formik';
import {
  ConfirmationTypes,
  ServiceTypes,
  adminCancelBooking,
  adminDeleteAvailability,
  adminRequestCancellation,
  deleteMulipleWorkshift,
  deletePharmacyWorkshift,
} from '@pharmaplan/common';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import { useAppDispatch } from '../useAppDispatch';
import { setDialog } from '../../reducers/dialogReducer';
import DeactivateDialog from '../../components/Admin/PharmacistList/DeactivateDialog';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import strings from '../../localization';
import { DeactivateModalIcon } from '../../helpers/Constants';
import ValidatorCancellation from '../../components/Admin/PharmacistList/DeactivateDialog/validator';
import CancellationDateTime from '../../components/Admin/PharmacyList/ListOfWorkshifts/CancellationDateTime';
import useCancelMultipleBooking from '../useCancelMultipleBooking';
import MultiShiftDelete from '../../components/MultiShiftDelete';

const isSelfService = ServiceHandler.getService() === ServiceTypes.self;

export type ILongTermDeleteDetails = {
  longTermShiftContractId: string;
};

export type IShowCancellationParams = {
  longTermShiftContractId?: string;
  professionalId?: string;
  bookingId: string;
  dateTime: string;
};

const useWorkshiftDelete = () => {
  const dispatch = useAppDispatch();
  const { showCancellationDialog } = useCancelMultipleBooking();

  const requestCancellation = (values: FormikValues) => {
    const { bookingId, reason } = values ?? {};
    dispatch(adminRequestCancellation({ reason, bookingId }));
  };

  const showRequestCancellation = (bookingId: string, dateTime: string) => {
    dispatch(
      setDialog({
        Component: (
          <DeactivateDialog
            onSubmit={requestCancellation}
            confirmationMessage={strings.confirmWorkshiftCancel}
            id={bookingId}
            initialValues={{
              bookingId,
              reason: '',
            }}
            heading={strings.requestCancellation}
            iconType={DeactivateModalIcon.warning}
            validator={ValidatorCancellation()}
            name="reason"
            customComponent={<CancellationDateTime date={dateTime} />}
          />
        ),
        heading: {
          title: '',
        },
        showCloseButton: true,
      }),
    );
  };

  const confirmSelfServiceCancelBooking = (bookingId: string) =>
    () => {
      dispatch(adminCancelBooking({ bookingId }));
    };

  const showSelfServiceCancelBooking = (bookingId: string) => {
    dispatch(
      setDialog({
        Component: (
          <ConfirmationModal
            type={ConfirmationTypes.booking}
            confirmAction={confirmSelfServiceCancelBooking(bookingId)}
          />
        ),
        heading: {
          title: '',
        },
        showCloseButton: false,
      }),
    );
  };

  const showCancellation = ({
    longTermShiftContractId,
    professionalId,
    bookingId,
    dateTime,
  }: IShowCancellationParams) => {
    if (longTermShiftContractId) {
      showCancellationDialog({ longTermShiftContractId, professionalId });
      return;
    }

    if (isSelfService) {
      showSelfServiceCancelBooking(bookingId);
      return;
    }

    showRequestCancellation(bookingId, dateTime);
  };

  const handleWorkshiftDelete = (values: FormikValues) => {
    const { notes, workshiftIds } = values ?? {};
    dispatch(deletePharmacyWorkshift({ workshiftId: workshiftIds, notes }));
  };

  const handleMultipleDelete = (values: FormikValues) => {
    const { notes, workshiftIds } = values ?? {};
    dispatch(deleteMulipleWorkshift({ workshiftIds, notes }));
  };

  const longTermDelete = (
    longTermShiftContractId: string,
  ) => {
    dispatch(setDialog({
      heading: {
        title: strings.deleteShifts,
      },
      Component: (
        <MultiShiftDelete
          longTermShiftContractId={longTermShiftContractId ?? ''}
        />
      ),
      maxWidth: 'lg',
      showCloseButton: true,
    }));
  };

  const showWorkshiftDelete = (
    workshiftId: string,
    dateTime: string,
    longTermShiftContractId?: string,
  ) => {
    if (longTermShiftContractId) {
      longTermDelete(longTermShiftContractId);
      return;
    }

    dispatch(
      setDialog({
        Component: (
          <DeactivateDialog
            onSubmit={handleWorkshiftDelete}
            confirmationMessage={strings.confirmDeleteWorkshift}
            id={workshiftId}
            initialValues={{
              workshiftIds: workshiftId,
              notes: '',
            }}
            heading={strings.deleteWorkshift}
            iconType={DeactivateModalIcon.warning}
            name="notes"
            validator={null}
            customComponent={<CancellationDateTime date={dateTime} />}
          />
        ),
        heading: {
          title: '',
        },
        showCloseButton: true,
      }),
    );
  };

  const deleteAvailability = (values: FormikValues) => {
    const { notes, availabilityId } = values ?? {};
    dispatch(
      adminDeleteAvailability({ availabilityIds: [availabilityId], notes }),
    );
  };

  const showDeleteAvailability = (availabilityId: string, dateTime: string) => {
    dispatch(
      setDialog({
        Component: (
          <DeactivateDialog
            onSubmit={deleteAvailability}
            confirmationMessage={strings.confirmDeleteAvailability}
            id={availabilityId}
            initialValues={{
              availabilityId,
              notes: '',
            }}
            heading={strings.deleteAvailability}
            iconType={DeactivateModalIcon.warning}
            validator={null}
            name="notes"
            customComponent={<CancellationDateTime date={dateTime} />}
          />
        ),
        heading: {
          title: '',
        },
        showCloseButton: true,
      }),
    );
  };

  const showMultipleWorkshiftsDelete = (
    workshiftIds: Array<string>,
    customCount?: number,
  ) => {
    dispatch(
      setDialog({
        Component: (
          <DeactivateDialog
            onSubmit={handleMultipleDelete}
            confirmationMessage={
              strings.formatString(
                strings.confirmDeleteCountWorkshift,
                customCount ?? workshiftIds?.length,
              ) as string
            }
            id=""
            initialValues={{
              workshiftIds,
              notes: '',
            }}
            heading={`${strings.deleteWorkshift}s`}
            iconType={DeactivateModalIcon.warning}
            name="notes"
            validator={null}
          />
        ),
        heading: {
          title: '',
        },
        showCloseButton: true,
      }),
    );
  };

  return {
    showWorkshiftDelete,
    showRequestCancellation: showCancellation,
    showDeleteAvailability,
    showMultipleWorkshiftsDelete,
    deleteAvailability,
  };
};

export default useWorkshiftDelete;
